import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import { supabase } from '../supabaseClient';

// Loads the Stripe Pricing Table script once per page
function useStripePricingTableScript() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    // Check if script is already present
    if (document.querySelector('script[data-stripe-pricing-table]')) {
      setLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    script.setAttribute('data-stripe-pricing-table', 'true');
    script.onload = () => setLoaded(true);
    script.onerror = () => setLoaded(false);
    document.body.appendChild(script);
    return () => {
      // keep script for subsequent renders; do not remove
    };
  }, []);
  return loaded;
}

export default function StripePricingTable({ currentUser }) {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const pricingTableId = process.env.REACT_APP_STRIPE_PRICING_TABLE_ID;
  const scriptLoaded = useStripePricingTableScript();
  const tableRef = useRef(null);
  const [companyId, setCompanyId] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [error, setError] = useState('');

  const appUrl = useMemo(() => (process.env.REACT_APP_APP_URL || (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_APP_URL) || ''), []);
  const successUrl = useMemo(() => appUrl ? `${appUrl}/billing/success?session_id={CHECKOUT_SESSION_ID}` : '', [appUrl]);
  const cancelUrl = useMemo(() => appUrl ? `${appUrl}/pricing?canceled=1` : '', [appUrl]);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds

    const fetchCompanyId = async () => {
      setLoadingCompany(true);
      setError('');
      
      const attemptFetch = async () => {
        try {
          const { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .select('company_id')
            .eq('user_id', currentUser.id)
            .maybeSingle();
          
          if (profileError) throw profileError;
          
          // If no company_id found and we haven't exhausted retries, retry
          if (!profile?.company_id && retryCount < maxRetries) {
            retryCount++;
            console.log(`[StripePricingTable] No company_id found, retrying... (${retryCount}/${maxRetries})`);
            setTimeout(() => {
              if (isMounted) attemptFetch();
            }, retryDelay);
            return;
          }
          
          // Found company_id or exhausted retries
          if (isMounted) {
            setCompanyId(profile?.company_id || null);
            setLoadingCompany(false);
          }
        } catch (e) {
          if (isMounted) {
            setError(e.message || 'Failed to load company context');
            setLoadingCompany(false);
          }
        }
      };

      attemptFetch();
    };

    if (currentUser?.id) fetchCompanyId();
    return () => {
      isMounted = false;
    };
  }, [currentUser?.id]);

  const clientReferenceId = useMemo(() => {
    try {
      if (!currentUser?.id || !companyId) return null;
      // Stripe client-reference-id only allows alphanumeric, spaces, dashes, underscores
      // Format: user_id-company_id (safe for Stripe)
      return `${currentUser.id}-${companyId}`;
    } catch (_) {
      return null;
    }
  }, [currentUser?.id, companyId]);

  if (!publishableKey || !pricingTableId) {
    console.log('[StripePricingTable] Missing config:', { publishableKey: !!publishableKey, pricingTableId: !!pricingTableId });
    return (
      <Alert severity="warning">Stripe pricing table is not configured.</Alert>
    );
  }

  if (!scriptLoaded || loadingCompany) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading pricing…</Typography>
      </Box>
    );
  }

  if (!companyId) {
    console.log('[StripePricingTable] No company context:', { currentUser: !!currentUser, companyId });
    return (
      <Alert severity="warning">No company context found. Please complete company setup first.</Alert>
    );
  }

  console.log('[StripePricingTable] Rendering with:', { 
    publishableKey: publishableKey?.slice(0, 12) + '...', 
    pricingTableId, 
    clientReferenceId: !!clientReferenceId,
    email: currentUser?.email,
    scriptLoaded,
    companyId,
    successUrl,
    cancelUrl
  });
  
  // Debug: log the actual HTML being rendered
  const htmlToRender = `<stripe-pricing-table
            pricing-table-id="${pricingTableId}"
            publishable-key="${publishableKey}"
            client-reference-id="${clientReferenceId || ''}"
            customer-email="${currentUser?.email || ''}"
            locale="en"
            success-url="${successUrl}"
            cancel-url="${cancelUrl}">
          </stripe-pricing-table>`;
  console.log('[StripePricingTable] HTML being rendered:', htmlToRender);

  return (
    <Box ref={tableRef} sx={{ width: '100%' }}>
      {/* Use dangerouslySetInnerHTML for custom element to ensure proper rendering */}
      <div
        dangerouslySetInnerHTML={{
          __html: `<stripe-pricing-table
            pricing-table-id="${pricingTableId}"
            publishable-key="${publishableKey}"
            client-reference-id="${clientReferenceId || ''}"
            customer-email="${currentUser?.email || ''}"
            locale="en"
            success-url="${successUrl}"
            cancel-url="${cancelUrl}">
          </stripe-pricing-table>`
        }}
      />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
}


